import { type CSSProperties, type FC } from 'react';
import { css, type Interpolation, type Theme } from '@emotion/react';
import { theme } from '@frontend/theme';

interface ProgressBarProps {
  barColor?: string;
  barStyles?: Interpolation<Theme>;
  borderRadius?: CSSProperties['borderRadius'];
  containerStyles?: Interpolation<Theme>;
  height?: CSSProperties['height'];
  progress: number;
  showEmptyProgress?: boolean;
  trackColor?: string;
  trackStyles?: Interpolation<Theme>;
}

const EMPTY_PROGRESS_WIDTH = 2;

export const ProgressBar: FC<ProgressBarProps> = ({
  barColor = theme.colors.secondary.seaweed50,
  barStyles,
  borderRadius = theme.borderRadius.small,
  containerStyles,
  height = theme.spacing(1.5),
  progress = 0,
  showEmptyProgress = true,
  trackColor = theme.colors.neutral5,
  trackStyles,
}) => {
  const normalizedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div css={[styles.container(borderRadius), containerStyles]}>
      <div
        aria-label='Task completion progress'
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={normalizedProgress}
        css={[styles.track({ borderRadius, bgColor: trackColor, height }), trackStyles]}
        role='progressbar'
      >
        <div
          css={[
            styles.bar({ bgColor: barColor, borderRadius, width: `${normalizedProgress}%` }),
            normalizedProgress === 0 && showEmptyProgress ? { aspectRatio: 1, width: EMPTY_PROGRESS_WIDTH } : {},
            barStyles,
          ]}
        />
      </div>
    </div>
  );
};

const styles = {
  container: (borderRadius: CSSProperties['borderRadius']) =>
    css({
      borderRadius,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      position: 'relative',
    }),

  track: ({
    bgColor,
    borderRadius,
    height,
  }: {
    bgColor: string;
    borderRadius?: CSSProperties['borderRadius'];
    height: CSSProperties['height'];
  }) =>
    css({
      backgroundColor: bgColor,
      borderRadius,
      boxSizing: 'content-box',
      height,
      width: '100%',
    }),

  bar: ({
    bgColor,
    borderRadius,
    width,
  }: {
    bgColor: string;
    borderRadius?: CSSProperties['borderRadius'];
    width: CSSProperties['width'];
  }) =>
    css({
      backgroundColor: bgColor,
      borderRadius,
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      transition: 'width 0.5s ease-in-out',
      width,
    }),
};
